import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Sparkles, RefreshCw, Clock3, ShieldCheck, Target, Store, ChevronDown, ChevronUp, Copy, Check, Megaphone, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { api, errorMessage } from "@/lib/api";
import { fmtTimeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";
import { fadeUp } from "@/components/exec/KpiTile";

const IMPACT = {
  high: "bg-gold/15 text-gold border-gold/30",
  medium: "bg-info/12 text-info border-info/25",
  low: "bg-muted/30 text-muted-foreground border-border/60",
};
const CONF = { high: 90, medium: 62, low: 35 };

const CATEGORY_LABEL = {
  trend: "Trend", calendar: "Calendar", demographics: "Who visits", walk_in: "Walk-in", cross_store: "Cross-store", risk: "Risk", opportunity: "Opportunity",
  conversion: "Conversion", basket: "Basket", product_mix: "Product mix", footfall_link: "Footfall link", traffic: "Traffic", staffing: "Staffing", range: "Range", pricing: "Pricing", cross_sell: "Cross-sell",
};

/* ------------------------------------------------------------------ cards */
export const InsightCard = ({ insight, index = 0 }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.article variants={fadeUp} initial="hidden" animate="show" custom={index} className="card-surface card-hover p-4 md:p-5 flex flex-col gap-3" data-testid="insight-card" data-impact={insight.impact}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={cn("font-mono text-[10px] uppercase tracking-wider", IMPACT[insight.impact] || IMPACT.medium)} data-testid="insight-card-impact">{insight.impact} impact</Badge>
        <Badge variant="outline" className="text-[10px] border-border/70 bg-muted/20">{CATEGORY_LABEL[insight.category] || insight.category}</Badge>
        {insight.stores?.length > 0 && <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Store size={11} /> {insight.stores.join(", ")}</span>}
      </div>
      <h4 className="font-display text-base md:text-[17px] leading-snug" data-testid="insight-card-headline">{insight.headline}</h4>
      <p className="text-sm text-foreground/85 leading-relaxed">{insight.why_it_matters}</p>
      {insight.evidence?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="insight-card-evidence">
          {insight.evidence.slice(0, open ? 10 : 2).map((e, i) => (
            <span key={i} className="text-[11px] font-mono rounded-md border border-border/70 bg-muted/20 px-2 py-0.5 text-muted-foreground">{e}</span>
          ))}
          {insight.evidence.length > 2 && (
            <button type="button" onClick={() => setOpen(!open)} className="text-[11px] text-gold hover:underline inline-flex items-center gap-0.5 focus-gold rounded" data-testid="insight-card-view-evidence-button">
              {open ? <>less <ChevronUp size={11} /></> : <>+{insight.evidence.length - 2} more <ChevronDown size={11} /></>}
            </button>
          )}
        </div>
      )}
      <div className="mt-auto pt-2 border-t border-border/50 flex flex-col gap-2">
        {insight.action && (
          <div className="text-xs flex items-start gap-2"><Target size={13} className="text-gold mt-0.5 shrink-0" /><span><span className="text-muted-foreground">Next step: </span>{insight.action}</span></div>
        )}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <ShieldCheck size={12} /> Confidence {insight.confidence}
          <Progress value={CONF[insight.confidence] ?? 60} className="h-1 w-20 bg-muted/40 [&>div]:bg-gold" />
        </div>
      </div>
    </motion.article>
  );
};

export const RecommendationCard = ({ rec, index = 0 }) => (
  <motion.article variants={fadeUp} initial="hidden" animate="show" custom={index} className="card-surface card-hover p-4 md:p-5 flex flex-col gap-3" data-testid="ai-recommendation-card" data-impact={rec.impact}>
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant="outline" className={cn("font-mono text-[10px] uppercase tracking-wider", IMPACT[rec.impact] || IMPACT.medium)}>{rec.impact} impact</Badge>
      {rec.cross_sell && <Badge variant="outline" className="text-[10px] border-teal/30 bg-success/10 text-success gap-1"><Zap size={10} /> Cross-sell</Badge>}
      <Badge variant="outline" className="text-[10px] border-border/70 bg-muted/20">{CATEGORY_LABEL[rec.category] || rec.category}</Badge>
    </div>
    <h4 className="font-display text-base md:text-[17px] leading-snug" data-testid="ai-recommendation-title">{rec.title}</h4>
    <div className="rounded-lg border border-gold/25 bg-gold/8 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Expected impact · {rec.kpi}</div>
      <div className="text-sm font-medium mt-0.5" data-testid="ai-recommendation-impact">{rec.expected_impact}</div>
    </div>
    <p className="text-sm text-foreground/85 leading-relaxed">{rec.rationale}</p>
    {rec.evidence?.length > 0 && (
      <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
        {rec.evidence.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}
      </ul>
    )}
    <div className="mt-auto pt-2 border-t border-border/50 text-xs flex items-start gap-2">
      <Target size={13} className="text-gold mt-0.5 shrink-0" />
      <span><span className="text-muted-foreground">This week: </span>{rec.first_step}</span>
    </div>
    {rec.stores?.length > 0 && <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Store size={11} /> {rec.stores.join(" · ")}</div>}
  </motion.article>
);

export const PromotionCard = ({ promo, index = 0 }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const text = [`${promo.title}`, `Bundle: ${promo.bundle}`, `Offer: ${promo.offer_wording}`, `Mechanics: ${promo.mechanics}`, `Timing: ${promo.timing}`, `Target: ${promo.target_segment}`, `Stores: ${(promo.stores || []).join(", ")}`, `Success metric: ${promo.success_metric}`, `Risks: ${promo.risks}`].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Promotion brief copied");
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Clipboard unavailable");
    }
  };
  return (
    <motion.article variants={fadeUp} initial="hidden" animate="show" custom={index} className="card-surface card-hover p-4 md:p-5" data-testid="promotion-draft-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-8 w-8 rounded-lg bg-gold/15 text-gold flex items-center justify-center shrink-0"><Megaphone size={15} /></span>
          <h4 className="font-display text-base leading-snug truncate" data-testid="promotion-draft-title">{promo.title}</h4>
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 shrink-0" onClick={copy} data-testid="promotion-draft-copy-button">{copied ? <Check size={13} /> : <Copy size={13} />} Copy brief</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div className="space-y-3">
          <Field label="Bundle">{promo.bundle}</Field>
          <Field label="Offer wording"><span className="italic">“{promo.offer_wording}”</span></Field>
          <Field label="Mechanics">{promo.mechanics}</Field>
        </div>
        <div className="space-y-3">
          <Field label="Timing">{promo.timing}</Field>
          <Field label="Target segment">{promo.target_segment}</Field>
          <Field label="Stores">{(promo.stores || []).join(" · ") || "—"}</Field>
          <Field label="Success metric">{promo.success_metric}</Field>
          {promo.risks && <Field label="Risks"><span className="text-muted-foreground">{promo.risks}</span></Field>}
        </div>
      </div>
    </motion.article>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
    <div className="leading-relaxed mt-0.5">{children}</div>
  </div>
);

/* ------------------------------------------------------------------ status + generate + poll */
export const useInsights = (kind, params, enabled = true) => {
  const qc = useQueryClient();
  const key = ["insights-v2", kind, params];
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.getInsightsV2(kind, params),
    enabled,
    refetchInterval: (q) => (q.state.data?.status === "generating" ? 4000 : false),
  });
  const [starting, setStarting] = useState(false);
  const generate = async (force = false) => {
    setStarting(true);
    try {
      const doc = await api.generateInsightsV2(kind, params, force);
      qc.setQueryData(key, (old) => ({ ...(old || {}), ...doc }));
      if (doc.status === "generating") toast.info("Generating insights… this takes about 30 seconds");
      else if (doc.reused) toast.success("Insights are already current for this view");
      query.refetch();
    } catch (err) {
      toast.error(errorMessage(err, "Could not start generation"));
    } finally {
      setStarting(false);
    }
  };
  return { ...query, generate, starting };
};

export const InsightsHeaderActions = ({ state, onGenerate, canGenerate = true }) => {
  const generating = state?.status === "generating" || state?.starting;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {state?.generated_at && (
        <span className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1" data-testid="insights-generated-at">
          <Clock3 size={11} /> {fmtTimeAgo(state.generated_at)}{state.trigger === "nightly" ? " · nightly" : ""}{state.stale ? " · data moved" : ""}
        </span>
      )}
      {canGenerate && (
        <Button size="sm" variant={state?.status === "ready" ? "outline" : "default"} className="h-8 gap-1.5" disabled={generating} onClick={() => onGenerate(state?.status === "ready")} data-testid="insights-generate-button">
          {generating ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {generating ? "Generating…" : state?.status === "ready" ? (state.stale ? "Refresh insights" : "Regenerate") : "Generate insights"}
        </Button>
      )}
    </div>
  );
};

export const InsightsBody = ({ state, kind = "count", emptyHint, renderReady }) => {
  if (!state || state.isLoading) return <InsightsSkeleton />;
  if (state.status === "generating") return <InsightsSkeleton label="The consultant is reading the evidence pack…" />;
  if (state.status === "error")
    return (
      <div className="rounded-xl border border-danger/30 bg-danger/10 p-4 text-sm flex items-start gap-2" data-testid="insights-error">
        <AlertTriangle size={15} className="text-danger mt-0.5" /> <span>Generation failed: {state.error}. Try again.</span>
      </div>
    );
  if (state.status !== "ready" || !state.data)
    return (
      <div className="rounded-xl border border-dashed border-border/70 bg-muted/10 p-6 text-sm text-muted-foreground" data-testid="insights-empty">
        {state.has_counts === false
          ? "No footfall data in this period yet - insights need at least one day of counts."
          : kind === "sales" && state.has_sales === false
            ? "No POS data in this period. Upload the POS Daily Sales Item report (admins) to unlock sales insights."
            : emptyHint || "No insights generated for this view yet. Standard week and month views are prepared every night at 06:00 MYT; press Generate for this one now."}
      </div>
    );
  return renderReady(state.data);
};

export const InsightsSkeleton = ({ label }) => (
  <div className="space-y-3" data-testid="insights-skeleton">
    {label && <div className="text-xs text-muted-foreground inline-flex items-center gap-2"><RefreshCw size={12} className="animate-spin" /> {label}</div>}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="card-surface p-4 space-y-3 shimmer">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-12 w-full" />
        </div>
      ))}
    </div>
  </div>
);

export const ExecutiveSummaryText = ({ text, watchlist }) =>
  text ? (
    <div className="rounded-xl border border-gold/25 bg-gold/8 p-4 md:p-5" data-testid="insights-executive-summary">
      <div className="eyebrow mb-2 inline-flex items-center gap-1.5"><Sparkles size={12} className="text-gold" /> Executive summary</div>
      <p className="text-sm md:text-[15px] leading-relaxed">{text}</p>
      {watchlist?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {watchlist.map((w, i) => <span key={i} className="text-[11px] rounded-md border border-border/70 bg-background/60 px-2 py-0.5 text-muted-foreground">Watch: {w}</span>)}
        </div>
      )}
    </div>
  ) : null;

/** Scrolls into view when `active` flips true (used for deep links). */
export const useScrollOnActive = (active) => {
  const ref = useRef(null);
  useEffect(() => {
    if (active && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [active]);
  return ref;
};
